import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import DatePicker from "react-datepicker";
import { es } from "date-fns/locale";
import { useTranslation } from "react-i18next";
import { lockedDialogSlotProps } from "../../utils/lockedDialogUi";
import EnvioDestinoFields, {
  emptyEnvioDestino,
  isEnvioDestinoValid,
  DESTINO_CLIENTE,
  DESTINO_PROVEEDOR,
  DESTINO_INTERNO,
} from "./EnvioDestinoFields";
import {
  listCourierPendientes,
  createCourierDesdeMuestras,
} from "../../api/CourierControl/courierControlApi";
import { formatMuestraDateForAPI } from "../../utils/dateUtils";
import { requiresCourierOnShipment } from "./Drawer/muestraEstadoHelpers";

export default function EnviarJuntasCourierDialog({
  open,
  onClose,
  muestraIds = [],
  isChinaUser = false,
  onDone,
}) {
  const { t } = useTranslation();
  const [form, setForm] = useState({
    ...emptyEnvioDestino(),
    TIPO_DESTINO: DESTINO_CLIENTE,
    FECHA_ENVIO: null,
    NUM_COURIER: "",
  });
  const [attachMode, setAttachMode] = useState("new");
  const [attachCourierId, setAttachCourierId] = useState("");
  const [pendientes, setPendientes] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) return undefined;
    const today = new Date();
    today.setHours(12, 0, 0, 0);
    setForm({
      ...emptyEnvioDestino(),
      TIPO_DESTINO: DESTINO_CLIENTE,
      FECHA_ENVIO: today,
      NUM_COURIER: "",
    });
    setAttachMode("new");
    setAttachCourierId("");
    setError("");
    let cancelled = false;
    listCourierPendientes()
      .then((rows) => {
        if (!cancelled) setPendientes(Array.isArray(rows) ? rows : []);
      })
      .catch(() => {
        if (!cancelled) setPendientes([]);
      });
    return () => {
      cancelled = true;
    };
  }, [open]);

  const handleConfirm = async () => {
    setError("");
    if (
      !form.FECHA_ENVIO ||
      !isEnvioDestinoValid(form, {
        requireContact: form.TIPO_DESTINO !== DESTINO_INTERNO,
      })
    ) {
      setError(t("Samples.completeDestinationAndPerson"));
      return;
    }
    if (
      attachMode === "attach" &&
      (!attachCourierId || !Number(attachCourierId))
    ) {
      setError(t("Samples.courierAttachPickRequired"));
      return;
    }
    if (
      requiresCourierOnShipment({ isChinaUser }) &&
      !String(form.NUM_COURIER || "").trim() &&
      attachMode !== "attach"
    ) {
      setError(t("Samples.courierRequiredChina"));
      return;
    }

    setSaving(true);
    try {
      let numCourier = form.NUM_COURIER?.trim() || null;
      const idCourierExistente =
        attachMode === "attach" ? Number(attachCourierId) : null;
      if (idCourierExistente && !numCourier) {
        const pending = pendientes.find(
          (p) => Number(p.IdCourierControl) === idCourierExistente,
        );
        if (pending?.NumeroAwb) numCourier = pending.NumeroAwb;
      }

      const result = await createCourierDesdeMuestras({
        muestraIds,
        TIPO_DESTINO: form.TIPO_DESTINO,
        ID_CLIENTE: form.ID_CLIENTE,
        CODPROV: form.CODPROV,
        ID_CONTACTO: form.ID_CONTACTO,
        PERSONA_ENVIO: form.PERSONA_ENVIO || null,
        FECHA_ENVIO: formatMuestraDateForAPI(form.FECHA_ENVIO),
        NUM_COURIER: numCourier,
        ...(idCourierExistente ? { idCourierExistente } : {}),
      });
      if (onDone) await onDone(result);
      onClose();
    } catch (err) {
      setError(
        err?.response?.data?.error ||
          err?.message ||
          t("Samples.sentToClientShipmentError"),
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={(_e, reason) => {
        if (reason === "backdropClick" || reason === "escapeKeyDown") return;
        if (saving) return;
        onClose?.();
      }}
      disableEscapeKeyDown
      slotProps={lockedDialogSlotProps}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>{t("Samples.courierSendTogetherTitle")}</DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ mb: 2 }}>
          {t("Samples.courierSendTogetherBody", { count: muestraIds.length })}
        </DialogContentText>

        <EnvioDestinoFields
          value={form}
          onChange={(next) => setForm((prev) => ({ ...prev, ...next }))}
          disabled={saving}
          allowedTipos={[DESTINO_CLIENTE, DESTINO_PROVEEDOR, DESTINO_INTERNO]}
        />

        <Box sx={{ mt: 2 }}>
          <DatePicker
            selected={form.FECHA_ENVIO ? new Date(form.FECHA_ENVIO) : null}
            locale={es}
            dateFormat="dd/MM/yyyy"
            onChange={(date) => {
              if (!date) {
                setForm((prev) => ({ ...prev, FECHA_ENVIO: null }));
                return;
              }
              const safe = new Date(date);
              safe.setHours(12, 0, 0, 0);
              setForm((prev) => ({ ...prev, FECHA_ENVIO: safe }));
            }}
            customInput={<TextField fullWidth label={t("Samples.sendDate")} />}
          />
        </Box>

        <TextField
          fullWidth
          margin="normal"
          required={
            requiresCourierOnShipment({ isChinaUser }) &&
            attachMode !== "attach"
          }
          label={t("Samples.courierNumber")}
          value={form.NUM_COURIER || ""}
          onChange={(e) =>
            setForm((prev) => ({ ...prev, NUM_COURIER: e.target.value }))
          }
        />

        {pendientes.length > 0 ? (
          <Box sx={{ mt: 1.5 }}>
            <FormControl fullWidth size="small" sx={{ mb: 1.25 }}>
              <InputLabel id="multi-courier-attach-mode">
                {t("Samples.courierBoxMode")}
              </InputLabel>
              <Select
                labelId="multi-courier-attach-mode"
                label={t("Samples.courierBoxMode")}
                value={attachMode}
                disabled={saving}
                onChange={(e) => {
                  setAttachMode(e.target.value);
                  if (e.target.value === "new") setAttachCourierId("");
                }}
              >
                <MenuItem value="new">{t("Samples.courierBoxNew")}</MenuItem>
                <MenuItem value="attach">
                  {t("Samples.courierBoxAttach")}
                </MenuItem>
              </Select>
            </FormControl>
            {attachMode === "attach" ? (
              <FormControl fullWidth size="small">
                <InputLabel id="multi-courier-attach-id">
                  {t("Samples.courierBoxPick")}
                </InputLabel>
                <Select
                  labelId="multi-courier-attach-id"
                  label={t("Samples.courierBoxPick")}
                  value={attachCourierId}
                  disabled={saving}
                  onChange={(e) => {
                    const id = e.target.value;
                    setAttachCourierId(id);
                    const pending = pendientes.find(
                      (p) => String(p.IdCourierControl) === String(id),
                    );
                    if (pending?.NumeroAwb) {
                      setForm((prev) => ({
                        ...prev,
                        NUM_COURIER: pending.NumeroAwb,
                      }));
                    }
                  }}
                >
                  {pendientes.map((row) => (
                    <MenuItem
                      key={row.IdCourierControl}
                      value={String(row.IdCourierControl)}
                    >
                      {[
                        String(row.Fecha || "").slice(0, 10),
                        row.Destino || row.Origen || "",
                        row.NumeroAwb || t("courierControl.pendingBox"),
                        `${row.piezas || 0} ${t("courierControl.pieces")}`,
                      ]
                        .filter(Boolean)
                        .join(" · ")}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            ) : null}
          </Box>
        ) : null}

        {error ? (
          <Typography color="error" variant="body2" sx={{ mt: 1.5 }}>
            {error}
          </Typography>
        ) : null}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          {t("Samples.cancel")}
        </Button>
        <Button variant="contained" onClick={handleConfirm} disabled={saving}>
          {saving ? t("Samples.saving") : t("Samples.confirm")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
